import { useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, QrCode, DollarSign, Calendar } from "lucide-react";
import ImageField from "@/components/ImageField";

const BUSINESS_TYPES = [
  "Coffee Shop", "Barbershop", "Boutique", "Restaurant", "Office",
  "Waiting Room", "Market Booth", "Event Vendor", "Public Space", "Other",
];
const PIECE_COUNTS = ["1", "2-3", "4-6", "7-10", "10+"];

export default function PartnerApply() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    address: "", city: "", state: "", zip: "", website: "",
    social_links: { facebook: "", instagram: "", tiktok: "" },
    business_type: "", requested_piece_count: "",
    display_area_description: "", reason_for_applying: "",
    preferred_commission: "", notes: "",
    uploaded_photos: [],
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setSocial = (k, v) => setForm({ ...form, social_links: { ...form.social_links, [k]: v } });
  const addPhoto = (url) => set("uploaded_photos", [...form.uploaded_photos, url]);
  const rmPhoto = (i) => set("uploaded_photos", form.uploaded_photos.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/partner/apply", form);
      setSent(data);
      toast.success("Application sent. Mickey will be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send application.");
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <Layout>
        <section className="bg-[#050505] py-32 px-6">
          <div className="max-w-xl mx-auto border border-emerald-700/40 bg-emerald-900/10 p-10 text-center">
            <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-4" />
            <h2 className="font-display text-3xl text-[#F4F1EA] mb-2">APPLICATION SENT.</h2>
            <p className="font-body text-[#B5B0A7]">
              Mickey will personally review your application and reach out
              within a few business days.
            </p>
            <p className="font-body text-[12px] text-[#8a857d] mt-6">
              Save this link to check your status anytime:
            </p>
            <code className="block bg-[#0a0a0a] border border-white/10 p-2 mt-2 text-[#C9A055] text-[12px] break-all">
              simpletruthsclub.com/partner-apply/status/{sent.status_token}
            </code>
            <button className="btn-outline mt-6"
              onClick={() => nav(`/partner-apply/status/${sent.status_token}`)}
              data-testid="pa-view-status">
              View My Status
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-[#050505] pt-16 pb-12">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-3">Become a Host</div>
          <h1 className="font-display text-5xl md:text-7xl text-[#F4F1EA] leading-[0.95] mb-4">
            PUT ART<br />ON YOUR WALL.
          </h1>
          <p className="font-script text-[#C9A055] text-2xl mb-3">Earn from every truth that sells.</p>
          <p className="font-body text-[#B5B0A7] max-w-2xl mx-auto">
            Display Simple Truths artwork in your business, booth, office, or
            public space. If approved, we&apos;ll provide the artwork, QR display
            signage, and a custom host agreement with your commission rate.
          </p>
        </div>
      </section>

      <section className="bg-[#050505] pb-12">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-4">
          {[
            { i: <QrCode size={26} />, t: "Display the Art", d: "Framed truths + QR signage delivered to your wall." },
            { i: <DollarSign size={26} />, t: "Earn Commission", d: "Custom commission rate negotiated in your offer." },
            { i: <Calendar size={26} />, t: "Track Performance", d: "Scans, sales, payouts — all visible in your dashboard." },
          ].map((c, i) => (
            <div key={i} className="border border-white/10 bg-[#0a0a0a] p-6">
              <div className="text-[#C9A055] mb-3">{c.i}</div>
              <div className="font-display text-2xl text-[#F4F1EA] mb-1">{c.t.toUpperCase()}</div>
              <p className="font-body text-[14px] text-[#B5B0A7]">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#050505] pb-24">
        <div className="max-w-[860px] mx-auto px-6 lg:px-10">
          <form onSubmit={submit} data-testid="partner-apply-form"
            className="border border-white/10 bg-[#0a0a0a] p-8 space-y-6">

            <Section title="Business / Location">
              <Field label="Business / Location Name *">
                <input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                  data-testid="pa-business" className="stc-input w-full" />
              </Field>
              <Row2>
                <Field label="Contact Name *">
                  <input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                    data-testid="pa-contact" className="stc-input w-full" />
                </Field>
                <Field label="Email *">
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                    data-testid="pa-email" className="stc-input w-full" />
                </Field>
              </Row2>
              <Row2>
                <Field label="Phone">
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="stc-input w-full" />
                </Field>
                <Field label="Website">
                  <input value={form.website} onChange={(e) => set("website", e.target.value)} className="stc-input w-full" />
                </Field>
              </Row2>
              <Field label="Business Address">
                <input value={form.address} onChange={(e) => set("address", e.target.value)} className="stc-input w-full" />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className="stc-input" />
                <input placeholder="State" value={form.state} onChange={(e) => set("state", e.target.value)} className="stc-input" />
                <input placeholder="Zip" value={form.zip} onChange={(e) => set("zip", e.target.value)} className="stc-input" />
              </div>
              <Row3>
                <Field label="Facebook">
                  <input value={form.social_links.facebook} onChange={(e) => setSocial("facebook", e.target.value)} className="stc-input w-full" />
                </Field>
                <Field label="Instagram">
                  <input value={form.social_links.instagram} onChange={(e) => setSocial("instagram", e.target.value)} className="stc-input w-full" />
                </Field>
                <Field label="TikTok">
                  <input value={form.social_links.tiktok} onChange={(e) => setSocial("tiktok", e.target.value)} className="stc-input w-full" />
                </Field>
              </Row3>
            </Section>

            <Section title="Your Display">
              <Row2>
                <Field label="Business Type">
                  <select value={form.business_type} onChange={(e) => set("business_type", e.target.value)}
                    data-testid="pa-business-type" className="stc-input w-full">
                    <option value="">Select…</option>
                    {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="How many pieces would you like?">
                  <select value={form.requested_piece_count} onChange={(e) => set("requested_piece_count", e.target.value)}
                    data-testid="pa-piece-count" className="stc-input w-full">
                    <option value="">Select…</option>
                    {PIECE_COUNTS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
              </Row2>
              <Field label="Tell us about your space (foot traffic, location type, vibe)">
                <textarea rows={3} value={form.display_area_description}
                  onChange={(e) => set("display_area_description", e.target.value)}
                  className="stc-input w-full font-body" />
              </Field>
              <Field label="Why do you want to host Simple Truths?">
                <textarea rows={3} value={form.reason_for_applying}
                  onChange={(e) => set("reason_for_applying", e.target.value)}
                  className="stc-input w-full font-body" />
              </Field>
              <Field label="Upload photos of your display area">
                <div className="grid sm:grid-cols-3 gap-2">
                  {form.uploaded_photos.map((u, i) => (
                    <div key={i} className="relative border border-white/10 p-1">
                      <img src={u} alt="" className="w-full h-32 object-cover" />
                      <button type="button" onClick={() => rmPhoto(i)}
                        className="absolute top-1 right-1 text-[10px] bg-black/70 text-red-300 px-2 py-0.5">remove</button>
                    </div>
                  ))}
                  <ImageField value="" onChange={addPhoto} label="Add photo" />
                </div>
              </Field>
              <Row2>
                <Field label="Preferred commission % (optional)">
                  <input value={form.preferred_commission} onChange={(e) => set("preferred_commission", e.target.value)}
                    placeholder="e.g. 15%" className="stc-input w-full" />
                </Field>
                <Field label="Notes / Questions">
                  <input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="stc-input w-full" />
                </Field>
              </Row2>
            </Section>

            <button disabled={loading} data-testid="pa-submit" className="btn-gold w-full py-4">
              {loading ? "Sending…" : "Apply To Become A Host"}
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">{label}</div>
      {children}
    </label>
  );
}
function Row2({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }
function Row3({ children }) { return <div className="grid sm:grid-cols-3 gap-3">{children}</div>; }
